import { useState, useEffect, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { Box, Grid } from '@mui/material'
import { Button, Typography } from '@carto/meridian-ds/components'
import { ArrowForward } from '@mui/icons-material'
import { MapEmptyCardProps } from 'features/maps/types'
import MapEmptyCard from 'features/maps/ui/MapEmptyCard/MapEmptyCard'
import mapThumbnail1 from 'assets/img/home/maps/background-cities01.png'
import mapThumbnail2 from 'assets/img/home/maps/background-cities02.png'
import mapThumbnail3 from 'assets/img/home/maps/background-cities03.png'
import useUserRoles from 'features/account/state/useUserRoles'
import MapsHardQuotaModal from 'features/maps/ui/MapsHardQuotaModal'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import useAccountHasMaps from 'features/maps/state/useAccountHasMaps'
import { QuotaUxTrigger } from 'features/hubspot/types'
import { BuilderPaths } from 'features/builder/paths'
import { openNewWindow } from 'features/common/utils/navigationUtils'

const emptyCardsImgs = [mapThumbnail1, mapThumbnail2, mapThumbnail3, mapThumbnail1]

export function HomeDynamicMaps({
  mapsLength,
  title,
  children,
  emptyMapWithButton,
  isOnboarding = false
}: {
  mapsLength: number
  title?: ReactNode
  children: ReactNode
  emptyMapWithButton?: boolean
  isOnboarding?: boolean
}) {
  const [totalMapsToShow, setTotalMapsToShow] = useState<number>(0)
  const { userIsViewer } = useUserRoles()
  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)
  const { accountHasMaps } = useAccountHasMaps()

  useEffect(() => {
    setTotalMapsToShow(4)
  }, [])

  const showPlaceholders = mapsLength === 0 ? true : mapsLength < totalMapsToShow && !userIsViewer

  const emptyCards = (cardsNumber: number) => {
    const emptyCards: MapEmptyCardProps[] = []

    for (let i = 0; i < cardsNumber; i++) {
      emptyCards.push({
        onClick: () => {
          if (hardQuotaModalController.additionWouldExceedQuota()) {
            hardQuotaModalController.open()
            return
          }
          openNewWindow(BuilderPaths.NewEmptyMap)
        },
        img: emptyCardsImgs[i]
      })
    }
    return emptyCards
  }

  return (
    <Box mb={8}>
      <Box display="flex" justifyContent="space-between" py={3}>
        <Typography variant="h6">{title ? title : <FormattedMessage id="home.actions.lastMaps.title" />}</Typography>
        {accountHasMaps && (
          <Button color="primary" component={NavLink} to="/maps" size="small" endIcon={<ArrowForward />}>
            <FormattedMessage id="home.actions.lastMaps.all" />
          </Button>
        )}
      </Box>
      <Grid container spacing={3}>
        {[
          ...(!isOnboarding ? [children] : []),
          ...(showPlaceholders
            ? emptyCards(totalMapsToShow - mapsLength).map((card, key) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                  <MapEmptyCard card={card} withButton={key === 0 && !children ? true : emptyMapWithButton} />
                </Grid>
              ))
            : []),
          ...(isOnboarding ? [children] : [])
        ]}
      </Grid>
      <MapsHardQuotaModal {...hardQuotaModalController.props} variant="create" quotaUxTrigger={QuotaUxTrigger.NewMap} />
    </Box>
  )
}

export default HomeDynamicMaps
